<template>
  <div class="brands-category">
    <h1 class="brands-category__title">
      {{ pageTitle }}
    </h1>

    <div class="brands-category__navigation">
      <div class="wrapper">
        <div class="brands-category__nav-groups">
          <a
            v-for="(brands, keyOfGroup) in getBrandsByGroups(data?.items ?? [])"
            :key="keyOfGroup"
            :href="getId(keyOfGroup, '#')"
            class="brands-category__nav-groups__item brands-category__link"
            @click.prevent="scrollToGroup(keyOfGroup)"
          >
            {{ keyOfGroup }}
          </a>
        </div>
        <UISearchInput
          v-model.trim="searchQuery"
          :placeholder="t('components.brands.categoryPage.searchQuery')"
          class="brands-category__input"
        />
      </div>
    </div>

    <div class="wrapper brands-category__groups">
      <div
        v-for="(brands, keyOfGroup) in brandsByGroups"
        :key="keyOfGroup"
        class="brands-category__group"
      >
        <div
          :id="getId(keyOfGroup)"
          class="brands-category__group__title"
        >
          {{ keyOfGroup }}
        </div>
        <div class="brands-category__group__items">
          <div
            v-for="brand in brands"
            :key="brand.name"
            class="brands-category__group__brand-item"
          >
            <nuxt-link
              :to="getUrlName(brand)"
              class="brands-category__group__brand-item-link brands-category__link"
            >
              {{ brand.name }}
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!filteredBrands?.length && searchQuery"
      class="brands-category__empty"
      v-html="t('components.brands.categoryPage.searchQuery.empty', {searchQuery: `<span>${searchQuery}</span>`})"
    />
  </div>
</template>

<script lang="ts" setup>
import { useCatalogProvider } from '~/providers/catalog';
import { useBrands } from '~/services/catalogFilter/brands';
import UISearchInput from '~/components/ui/UISearchInput.vue';
import { BrandDTO } from '~/restAPI/data-contracts';

const { t } = useI18n();
const route = useRoute();
const { fetchBrandsByCategory } = useCatalogProvider();
const { getBrandsByGroups } = useBrands();
const {
  parentCategoryWomenName,
  parentCategoryMenName,
  parentCategoryKidsName,
  parentCategoryLifestyleName,
  parentCategoriesNameForIdMap,
} = useCategories();

const { data } = await useAsyncData(route.path, () => fetchBrandsByCategory(parentCategoriesNameForIdMap.value[route.params.category]));

const searchQuery = ref('');

const filteredBrands = computed(() => {
  if (!searchQuery.value) return data.value?.items;

  return data.value?.items?.filter((brand: BrandDTO) => brand?.name?.toLowerCase()?.includes(searchQuery.value.toLowerCase()));
});

const brandsByGroups = computed(() => getBrandsByGroups(filteredBrands.value ?? []));

function getId(keyOfGroup: string, hash?: '#') {
  return `${hash || ''}char-${keyOfGroup.charCodeAt(0)}`;
}

function scrollToGroup(keyOfGroup: string) {
  const $group = document.querySelector(getId(keyOfGroup, '#'));

  $group?.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

function getUrlName(brand: BrandDTO) {
  if (!brand.urlName) return route.path;

  return `/catalog/${brand.urlName}/${route.params.category}`;
}

const titles: Record<string, string> = {
  [parentCategoryWomenName.value]: t('components.brands.categoryPage.titles.women'),
  [parentCategoryMenName.value]: t('components.brands.categoryPage.titles.men'),
  [parentCategoryKidsName.value]: t('components.brands.categoryPage.titles.kids'),
  [parentCategoryLifestyleName.value]: t('components.brands.categoryPage.titles.lifestyle'),
};
const pageTitle = computed(() => titles[`${route.params!.category}`]);

useHead({
  title: `${pageTitle.value} — ${t('common.OskellyOnlineStore')}`,
});
</script>

<style scoped lang="scss">
.brands-category {
  position: relative;

  .wrapper {
    padding: 0 16px;
  }

  &__title {
    font-size: 38px;
    text-align: center;
    font-family: "Playfair Display";
    text-transform: uppercase;
    color: #333;
    letter-spacing: 2px;

    @media (max-width: 768px) {
      font-size: 28px;
      letter-spacing: 1px;
    }
  }

  &__navigation {
    position: sticky;
    top: -50px;
    border-top: 1px solid #f2f2f2;
    border-bottom: 1px solid #f2f2f2;
    padding: 16px 0;
    margin-top: 44px;
    background: #fff;

    .wrapper {
      display: flex;
      justify-content: space-between;
      gap: 20px;

      @media (max-width: 1024px) {
        flex-direction: column;
        margin-top: 20px;
      }
    }
  }

  &__nav-groups {
    display: flex;
    flex-wrap: wrap;

    &__item {
      display: flex;
      height: 40px;
      align-items: center;
      padding: 0 5px;
      font-size: 18px;
      letter-spacing: .11px;
      white-space: nowrap;
    }

    @media (max-width: 1024px) {
      width: 100%;
      justify-content: center;
      order: 2;
    }
  }

  &__input {
    width: 420px;
    height: 40px;
    padding: 0 30px;
    background-position-x: 10px;
    background-size: 14px 15px;
    font-size: 14px;
    color: #333;

    @media (max-width: 1024px) {
      width: 100%;
    }
  }

  &__groups {
    margin-top: 44px;

    @media (max-width: 1024px) {
      margin-top: 20px;
    }
  }

  &__group {
    margin-bottom: 50px;

    @media (max-width: 400px) {
      margin-bottom: 20px;
    }

    &__title {
      font-size: 30px;
      font-weight: 700;
      letter-spacing: .11px;
    }

    &__items {
      padding: 20px 4px;
      column-count: 3;
      column-gap: 24px;

      @media (max-width: 768px) {
        column-count: 2;
      }

      @media (max-width: 400px) {
        column-count: 1;
      }
    }

    &__brand {
      &-item {
        margin-bottom: 10px;

        &-link {
          font-size: 16px;
          color: #333;
          line-height: 18px;
        }
      }
    }
  }

  &__link {
    transition: .2s;

    &:hover {
      color: #bdbdbd;
    }
  }

  &__empty {
    padding: 126px 0;
    font-size: 24px;
    letter-spacing: .11px;
    color: #333;
    text-align: center;

    span {
      font-weight: 700;
    }
  }
}
</style>
